/**
 * プロフィール編集 UI（フェーズ3・要件24）。
 *
 * ログイン中のみ表示。編集ボタン → モーダルで操作する。
 * - 表示名の編集（空不可・30文字上限）
 * - アイコンの表示/非表示トグル（アップロードは不可。IdP 画像を出す/隠すだけ）
 * - 「初期値に戻す」（表示名・アイコンを IdP の名前・画像へ戻す）
 *
 * 更新はすべて POST /api/profile（認証必須）。本人の Profiles のみ更新される。
 */
import { useState } from 'react';
import { postProfile } from '../api/client';
import { loadTokens } from './cognito';
import type { UseAuthResult } from './useAuth';
import type { Lang } from '../i18n/lang';
import { messages } from '../i18n/messages';
import { useMenuModal } from './userMenuContext';
import './profileControl.css';

/** 表示名の長さ上限（サーバー側 DISPLAY_NAME_MAX と一致させる）。 */
const DISPLAY_NAME_MAX = 30;

export interface ProfileControlProps {
  /** App で一度だけ呼んだ useAuth の結果を渡す。 */
  readonly auth: UseAuthResult;
  /**
   * 現在の保存済みプロフィール上書き（App が GET /api/profile で取得した値）。
   * モーダルを開いたときの初期表示に使う。未指定の項目は Google 値を既定にする。
   */
  readonly currentProfile?: {
    displayName?: string;
    avatarHidden?: boolean;
  };
  /**
   * プロフィール更新を上位へ通知する（案A・左下ログイン表示をマップと揃えるため）。
   * 保存時は編集後の値、リセット時は undefined（＝ Google 値に戻す）を渡す。
   */
  readonly onProfileChange?: (
    override: { displayName?: string; avatarHidden?: boolean } | undefined,
  ) => void;
  /** トリガーボタンの表示形態（menu=メニュー項目／floating=旧フロート）。 */
  readonly variant?: 'menu' | 'floating';
  readonly lang: Lang;
}

export function ProfileControl({
  auth,
  currentProfile,
  onProfileChange,
  variant = 'menu',
  lang,
}: ProfileControlProps) {
  const { enabled, user, loading } = auth;
  const loggedIn = enabled && !loading && !!user;
  const m = messages(lang);

  const [open, setOpen] = useMenuModal();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // 入力の初期値はログイン中ユーザー（IdP の名前）を使う。
  const [name, setName] = useState('');
  const [avatarHidden, setAvatarHidden] = useState(false);

  function idToken(): string | null {
    return loadTokens()?.id_token ?? null;
  }

  function openModal(): void {
    // 保存済みの表示名があればそれを初期表示する。無ければ Google 名を既定にする。
    setName(currentProfile?.displayName ?? user?.name ?? '');
    setAvatarHidden(currentProfile?.avatarHidden ?? false);
    setMsg(null);
    setOpen(true);
  }

  async function handleSave(): Promise<void> {
    const token = idToken();
    if (!token) {
      setMsg(m.profile.sessionExpired);
      return;
    }
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setMsg(m.profile.nameEmpty);
      return;
    }
    if (Array.from(trimmed).length > DISPLAY_NAME_MAX) {
      setMsg(m.profile.nameTooLong(DISPLAY_NAME_MAX));
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await postProfile(token, {
        display_name: trimmed,
        avatar_hidden: avatarHidden,
      });
      // 左下ログイン表示もマップと揃える（案A）
      onProfileChange?.({ displayName: trimmed, avatarHidden });
      setMsg(m.profile.saved);
      setTimeout(() => setOpen(false), 1500);
    } catch {
      setMsg(m.profile.saveFailed);
    } finally {
      setBusy(false);
    }
  }

  async function handleReset(): Promise<void> {
    const token = idToken();
    if (!token) {
      setMsg(m.profile.sessionExpired);
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await postProfile(token, { reset: true });
      // 入力も初期値へ戻す
      setName(user?.name ?? '');
      setAvatarHidden(false);
      // 左下ログイン表示も Google 値へ戻す（上書き解除）
      onProfileChange?.(undefined);
      setMsg(m.profile.resetDone);
    } catch {
      setMsg(m.profile.resetFailed);
    } finally {
      setBusy(false);
    }
  }

  if (!loggedIn) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className={variant === 'menu' ? 'user-menu__item' : 'profile-trigger'}
        onClick={openModal}
      >
        {m.profile.trigger}
      </button>

      {open && (
        <div className="profile-modal__overlay" onClick={() => setOpen(false)}>
          <div
            className="profile-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={m.profile.title}
          >
            <h2 className="profile-modal__title">{m.profile.title}</h2>

            <label className="profile-modal__field">
              <span className="profile-modal__label">{m.profile.displayName}</span>
              <input
                type="text"
                className="profile-modal__input"
                value={name}
                maxLength={DISPLAY_NAME_MAX}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
                placeholder={m.profile.namePlaceholder}
              />
              <span className="profile-modal__hint">
                {Array.from(name.trim()).length}/{DISPLAY_NAME_MAX}
              </span>
            </label>

            <label className="profile-modal__toggle">
              <input
                type="checkbox"
                checked={avatarHidden}
                onChange={(e) => setAvatarHidden(e.target.checked)}
                disabled={busy}
              />
              {m.profile.hideAvatar}
            </label>
            <p className="profile-modal__note">{m.profile.avatarNote}</p>

            {msg && <p className="profile-modal__msg">{msg}</p>}

            <div className="profile-modal__actions">
              <button
                type="button"
                className="profile-modal__save"
                onClick={() => void handleSave()}
                disabled={busy}
              >
                {m.profile.save}
              </button>
              <button
                type="button"
                className="profile-modal__reset"
                onClick={() => void handleReset()}
                disabled={busy}
              >
                {m.profile.reset}
              </button>
            </div>

            <button
              type="button"
              className="profile-modal__close"
              onClick={() => setOpen(false)}
            >
              {m.common.close}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
